/// <reference types="nwjs-types" />
import React, { useCallback, useContext, useMemo, useRef } from "react";

import logger from "../../lib/logger";
import axios, { AxiosRequestConfig } from "axios";
import { PDFDocument } from "pdf-lib";

import {
  useDigitalReceptionMetadata,
  usePrinter,
  usePrintingEnabled,
  usePrintingLabel,
} from "../../lib/hooks/useQueryBoard";
import { getApiUrl } from "../../lib/common/getApiUrl";

type ExtendedWindow = Window &
  typeof globalThis & {
    kp_AirPrinter_printHTMLString: (html: string) => void;
    kp_AirPrinter_printPdf: (pdf: any) => void;
    kp_AirPrinter_print: (file: any) => void;
    nw?: {
      Window?: {
        get: () => {
          print: (options: { silent: boolean; automatic: boolean }) => void;
        };
      };
    };
  };

/**
 * Logs an error message when the Brother Printer print operation fails.
 *
 * This function is used to handle errors that occur during the printing process
 * with the Brother Printer. It logs the error details using the application's
 * logging mechanism.
 *
 * @param {any} error - The error object containing details about the print failure.
 */
function kp_AirPrinter_printDidFailWithErrorEx(error: any) {
  logger.error(`[BrotherPrinter] Print failed with error: `, error);
}

const isDev = process.env.NODE_ENV === "development";
const CLIENT_TOKEN = isDev
  ? process.env.API_CLIENT_TOKEN
  : window.location.pathname.split("/")[2];
const BOARD_ID = isDev
  ? process.env.LOCALE_BOARD_ID
  : window.location.pathname?.split?.("/")?.[4] ?? null;

interface BrotherPrinterContextValue {
  print: (id: string, printWindowRef: Window | null) => void;
}

const BrotherPrinterContext = React.createContext<BrotherPrinterContextValue>(
  null!,
);

export const useBrotherPrinter = () => useContext(BrotherPrinterContext);

interface BrotherPrinterProviderProps {
  children: React.ReactNode;
}

/**
 * BrotherPrinterProvider is a context provider component that allows its children to access
 * the BrotherPrinterContext. This context provides a `print` method to handle printing documents.
 *
 * Usage:
 *
 * At the root level, wrap your application with the BrotherPrinterProvider:
 *
 * ```tsx
 * import BrotherPrinterProvider from "./context/BrotherPrinter";
 *
 * // providers
 * <BrotherPrinterProvider>
 *   // other providers
 * </BrotherPrinterProvider>
 * ```
 *
 * In the component where you need to use the print functionality, import the `useBrotherPrinterContext` hook:
 *
 * ```tsx
 * // at the top
 * import { useBrotherPrinterContext } from "../../context/BrotherPrinter";
 *
 * // in the code
 * const { print } = useBrotherPrinterContext();
 *
 * // use the print method
 * print(ownerId);
 * ```
 *
 * The `print` method will fetch the print document in the background and handle any errors.
 * The `print` method is void and does not return any value.
 */
export const BrotherPrinterProvider = React.memo<BrotherPrinterProviderProps>(
  (props) => {
    const { children } = props;
    const printFrameRef = useRef<HTMLIFrameElement | null>(null);
    const enableBrotherPrinter = usePrintingEnabled();
    const metadata = useDigitalReceptionMetadata();
    const printerName = usePrinter();
    const printingLabel = usePrintingLabel();

    const apiUrl = getApiUrl();

    const api = useMemo(() => {
      return axios.create({
        baseURL: `${apiUrl}/api/v1`,
        headers: {
          "client-token": CLIENT_TOKEN,
          "board-id": BOARD_ID,
        },
      });
    }, []);

    const getPrintDocument = useCallback(
      async <T = unknown,>(
        id: string,
        options?: AxiosRequestConfig<any> | undefined,
      ) => {
        try {
          if (!api) {
            throw new Error("API not found!");
          }

          const response = await api.get<T>(
            `/brother-printer/print/${id}`,
            options,
          );
          logger.debug(
            `[BrotherPrinter] Print request has been sent for id: ${id}`,
          );

          return response;
        } catch (error) {
          logger.error(`[BrotherPrinter] Print request failed: `, error);
          return undefined;
        }
      },
      [api],
    );

    const removePrintFrame = useCallback(() => {
      if (!printFrameRef.current) {
        logger.error(
          `[BrotherPrinter] Print frame not found on timeout close!`,
        );
        return;
      }

      try {
        printFrameRef.current.remove();
        printFrameRef.current = null;
      } catch (error) {
        logger.error(
          `[BrotherPrinter] Error while removing print frame: `,
          error,
        );
      }
    }, [printFrameRef.current]);

    const closePrint = useCallback(() => {
      if (!printFrameRef.current) {
        logger.error(`[BrotherPrinter] Print frame not found on close!`);
        return;
      }

      try {
        setTimeout(removePrintFrame);
      } catch (error) {
        logger.error(`[BrotherPrinter] Error while closing print: `, error);
      }
    }, [printFrameRef.current]);

    const startPrint = useCallback(() => {
      const printWindow = printFrameRef.current?.contentWindow;

      if (!printWindow) {
        return;
      }

      if (metadata?.nwPrint) {
        // mistake, use other method - printInNewNWWindow
        return;
      }

      printWindow.onbeforeprint = closePrint;
      printWindow.onafterprint = closePrint;
      printWindow?.print();
    }, [printFrameRef.current, metadata]);

    const printInWindow = (
      printWindow: Window | null,
      printDocument: string,
    ) => {
      try {
        setTimeout(() => {
          printWindow?.document?.write(printDocument);
        }, 100);

        setTimeout(() => {
          printWindow?.print();
        }, 200);

        setTimeout(() => {
          printWindow?.close();
        }, 300);
      } catch (error) {
        logger.error(`[BrotherPrinter] Error while printing: `, error);
      }
    };

    const printInNewNWWindow = useCallback(
      async (printDocument: Blob) => {
        try {
          console.log("Printing a document in a new NW window", printDocument);
          // @ts-expect-error - nw.require is available in NW.js runtime
          const fs = nw.require("fs");
          // @ts-expect-error - nw.require is available in NW.js runtime
          const path = nw.require("path");
          // @ts-expect-error - nw.require gives Node context process with reliable execPath
          const nwProcess = nw.require("process");

          const timestamp = Date.now();
          // Use NW.js app directory instead of OS temp dir, since chrome-extension
          // protocol can't access /tmp on Linux
          const appDir = path.dirname(nwProcess.execPath);
          const tempDir = path.join(appDir, "temp");
          if (!fs.existsSync(tempDir)) {
            fs.mkdirSync(tempDir, { recursive: true });
          }
          const tempPdfPath = path.join(tempDir, `print-${timestamp}.pdf`);
          const arrayBuffer = await printDocument.arrayBuffer();

          // Resize PDF to fit custom print dimensions if configured in settings (mm)
          const printWidth = printingLabel?.width;
          const printHeight = printingLabel?.height;
          let pdfBytes: Uint8Array;

          // lets resize it here in case print width/height are set
          if (printWidth && printHeight) {
            const targetWidthPt = (printWidth / 25.4) * 72;
            const targetHeightPt = (printHeight / 25.4) * 72;
            const srcDoc = await PDFDocument.load(arrayBuffer);
            const resizedDoc = await PDFDocument.create();
            const pages = await resizedDoc.copyPages(
              srcDoc,
              srcDoc.getPageIndices(),
            );
            for (const page of pages) {
              // Content is already rotated by the backend via transformation matrices,
              // so we only need to scale and fit to the target label size
              const { width: origW, height: origH } = page.getSize();
              const scale = Math.min(
                targetWidthPt / origW,
                targetHeightPt / origH,
              );
              page.setSize(targetWidthPt, targetHeightPt);
              page.scaleContent(scale, scale);
              const xOffset = (targetWidthPt - origW * scale) / 2;
              const yOffset = (targetHeightPt - origH * scale) / 2;
              page.translateContent(xOffset, yOffset);
              resizedDoc.addPage(page);
            }
            pdfBytes = await resizedDoc.save();
            console.log(`Resized PDF to ${printWidth}x${printHeight}mm`);
          } else {
            pdfBytes = new Uint8Array(arrayBuffer);
          }

          // write
          fs.writeFileSync(tempPdfPath, pdfBytes);
          console.log("Wrote PDF to temp file:", tempPdfPath);

          // Use file:// URL so Chromium can access the local file
          // (plain paths get prefixed with chrome-extension:// which can't reach the filesystem)
          const fileUrl = "file://" + tempPdfPath.replace(/\\/g, "/");
          nw.Window.open(
            fileUrl,
            {
              show: false, // Keeps the window completely invisible
              width: 1200,
              height: 1200,
            },
            (hiddenWin) => {
              console.log("New NW print window opened", hiddenWin);
              // Poll until the PDF is loaded in the window, since NW.js loaded event
              // doesn't fire for PDFs rendered by Chromium's PDF plugin
              const maxAttempts = 20; // 20 * 500ms = 10s max wait
              let attempts = 0;
              const checkReady = setInterval(() => {
                attempts++;
                try {
                  const doc = hiddenWin.window.document;
                  const readyState = doc?.readyState;
                  console.log(
                    `NW print window check #${attempts}, readyState: ${readyState}`,
                  );
                  if (readyState === "complete" || attempts >= maxAttempts) {
                    clearInterval(checkReady);
                    if (attempts >= maxAttempts) {
                      console.warn(
                        "NW print window: max wait reached, proceeding anyway",
                      );
                    }
                    console.log("NW print window ready");
                    // @ts-ignore - getPrinters is available in NW.js runtime
                    hiddenWin.getPrinters(function (
                      printers: { deviceName: string; isDefault: boolean }[],
                    ) {
                      console.log("Available printers: ", printers);
                      const defaultPrinter =
                        printers.find((p) => p.isDefault) || printers[0];
                      console.log("Default printer: ", defaultPrinter);
                      console.log("Setup printer", printerName);
                      console.log(
                        "Printing with printer:",
                        printerName || defaultPrinter.deviceName,
                      );
                      if (printerName || defaultPrinter) {
                        hiddenWin.print({
                          autoprint: true,
                          printer: printerName || defaultPrinter.deviceName,
                          headerFooterEnabled: false,
                          shouldPrintBackgrounds: false,
                        });
                      } else {
                        console.warn("No printers found on this system.");
                      }
                    });

                    // Close the hidden window a few seconds later to clean up memory
                    setTimeout(() => {
                      console.log("Closing NW print window after 10 seconds");
                      hiddenWin.close(true);
                      try {
                        fs.unlinkSync(tempPdfPath);
                      } catch (e) {
                        /* ignore */
                      }
                    }, 10000);
                  }
                } catch (e) {
                  // Window not ready yet, keep polling
                  console.log(
                    `NW print window check #${attempts}: not ready yet`,
                  );
                }
              }, 500);
            },
          );
        } catch (error) {
          console.error(
            `[BrotherPrinter] Error while printing in new NW window: `,
            error,
          );
          logger.error(
            `[BrotherPrinter] Error while printing in new NW window: `,
            error instanceof Error ? error.message : error,
          );
        }
      },
      [printerName],
    );

    const printInIFrame = (printDocument: Blob) => {
      try {
        const frame = document.createElement("iframe");
        printFrameRef.current = frame;

        frame.id = "print-frame";
        frame.onload = () => {
          startPrint();
        };
        frame.src = URL.createObjectURL(printDocument);
        frame.style = `
        visibility: hidden;
        opacity: 0;
        width: 350px;
        height: 212px;
        position: fixed;
      `;
        document.body.appendChild(printFrameRef.current);
      } catch (error) {
        logger.error(`[BrotherPrinter] Error while printing: `, error);
      }
    };

    const printInIOS = (printDocument: string) => {
      try {
        (window as ExtendedWindow).kp_AirPrinter_printPdf(printDocument);
      } catch (error) {
        console.log("Error while printing: ", error);
      }
    };

    /**
     * Prints a badge by its owner ID (member - employee or visitor, meeting attendee). It checks if the
     * BrotherPrinter is enabled in the digital reception before printing.
     *
     * For example, to print a meeting attendee badge, you need to use the meeting attendee ID:
     * ```
     * const attendeeId = accessEventData?.accessEvent?.owner?.id;
     * ```
     *
     * This function retrieves a print document using the provided ID and attempts to print it
     * by creating an iframe and appending it to the document body, opening a new window, directly
     * printing in the body, or using iOS printing based on the metadata configuration. If the document
     * is not found or an error occurs during the printing process, appropriate error messages are logged.
     *
     * @param {string} id - The ID of the document to be printed.
     * @param {Window | null} printWindowRef - Optional reference to a print window.
     * @returns {Promise<void>} A promise that resolves when the print process is initiated or an error occurs.
     */
    const print = useCallback(
      (id: string, printWindowRef: Window | null) => {
        if (!id || !enableBrotherPrinter) {
          return;
        }

        if (printWindowRef) {
          console.log("printWindowRef from above");
        }

        let printWindow: Window | null = null;
        if (metadata?.printRef === "window" && !printWindowRef) {
          printWindow = window.open("", "");
        }

        const options: AxiosRequestConfig | undefined = {
          ...(metadata?.printRef === "iframe" || !metadata?.printRef
            ? { responseType: "blob" }
            : {}),
          params: { rotation: metadata.printRotation },
        };

        getPrintDocument<string | Blob>(id, options).then((printDocument) => {
          if (!printDocument) {
            logger.error(`[BrotherPrinter] Print document not found!`);
            return;
          }

          if (metadata?.printRef === "window") {
            printInWindow(
              printWindowRef || printWindow,
              printDocument.data as string,
            );
            return;
          }

          if (metadata?.nwPrint) {
            printInNewNWWindow(printDocument.data as Blob);
            return;
          }

          if (metadata?.printRef === "printIOS") {
            printInIOS(printDocument.data as string);
            return;
          }

          printInIFrame(printDocument.data as Blob);
        });
      },
      [enableBrotherPrinter],
    );

    const value = useMemo(
      () => ({
        print,
      }),
      [print],
    );

    return (
      <BrotherPrinterContext.Provider value={value}>
        {children}
      </BrotherPrinterContext.Provider>
    );
  },
);

export default BrotherPrinterProvider;
